import React from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { LIMITS, orderedFields } from '../../utils/checkSets';

// ONE FORM FOR ANY CHECK SET. The inputs are drawn from the set's own field
// list — label, type, unit, options, mandatory — so the same form serves a
// set any doctor or hospital defines. Nothing here knows what a field means.
//
//   number  a decimal input with the unit beside it
//   text    free text (≤ 1000)
//   select  one of the set's options
//   multi   any of the set's options (toggle chips)
//   date    a calendar date
//   yesno   Yes / No (and Clear, so "not asked" stays possible)
//
// Controlled: `values` is the form state ({ fieldKey: input value }) and every
// change is reported as onChange(key, value). `errors` holds i18n keys (or a
// server sentence under `server`) per field key.

const inputBase = 'w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/40 focus:border-[#4ABDBD] disabled:bg-gray-50 disabled:text-gray-500';

function ReadingForm({ set, values = {}, errors = {}, onChange, disabled = false, idPrefix = 'reading' }) {
    const { t } = useTranslation();
    const fields = orderedFields(set);

    const errorText = (key) => {
        const e = errors?.[key];
        if (!e) return '';
        return e.startsWith?.('err_') ? t(`checkSets.${e}`) : e;
    };

    return (
        <div data-testid={`${idPrefix}-form`} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {fields.map((f) => {
                const id = `${idPrefix}-${f.key}`;
                const value = values?.[f.key];
                const err = errorText(f.key);
                const border = err ? 'border-red-300 bg-red-50/40' : 'border-gray-200 bg-white';
                const wide = f.type === 'text' || f.type === 'multi';
                return (
                    <div key={f.key} className={`flex flex-col gap-1 ${wide ? 'sm:col-span-2' : ''}`} data-testid={`${id}-field`}>
                        <label htmlFor={id} className="text-xs font-semibold text-gray-600">
                            {f.label}
                            {f.required && <span className="ml-0.5 text-red-500" aria-hidden="true">*</span>}
                            {f.type === 'number' && f.unit ? <span className="ml-1 font-normal text-gray-400">({f.unit})</span> : null}
                        </label>

                        {f.type === 'number' && (
                            <div className="flex items-center gap-2">
                                <input
                                    id={id}
                                    data-testid={id}
                                    type="text"
                                    inputMode="decimal"
                                    value={value ?? ''}
                                    disabled={disabled}
                                    aria-invalid={Boolean(err)}
                                    aria-required={f.required === true}
                                    onChange={(e) => onChange?.(f.key, e.target.value.replace(/[^0-9.,\-+eE]/g, '').slice(0, 20))}
                                    className={`${inputBase} ${border}`}
                                />
                                {f.unit && <span className="shrink-0 text-xs font-medium text-gray-500">{f.unit}</span>}
                            </div>
                        )}

                        {f.type === 'text' && (
                            <textarea
                                id={id}
                                data-testid={id}
                                rows={2}
                                maxLength={LIMITS.text}
                                value={value ?? ''}
                                disabled={disabled}
                                aria-invalid={Boolean(err)}
                                aria-required={f.required === true}
                                onChange={(e) => onChange?.(f.key, e.target.value.replace(/[<>]/g, ''))}
                                className={`${inputBase} ${border} resize-y`}
                            />
                        )}

                        {f.type === 'select' && (
                            <select
                                id={id}
                                data-testid={id}
                                value={value ?? ''}
                                disabled={disabled}
                                aria-invalid={Boolean(err)}
                                aria-required={f.required === true}
                                onChange={(e) => onChange?.(f.key, e.target.value)}
                                className={`tt-select ${inputBase} ${border}`}
                            >
                                <option value="">{t('checkSets.select_placeholder')}</option>
                                {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                            </select>
                        )}

                        {f.type === 'multi' && (
                            <div id={id} role="group" aria-label={f.label} data-testid={id} className={`flex flex-wrap gap-2 rounded-xl border p-2 ${border}`}>
                                {(f.options || []).map((o) => {
                                    const list = Array.isArray(value) ? value : [];
                                    const on = list.includes(o);
                                    return (
                                        <button
                                            key={o}
                                            type="button"
                                            aria-pressed={on}
                                            disabled={disabled}
                                            data-testid={`${id}-option-${o}`}
                                            onClick={() => onChange?.(f.key, on ? list.filter((x) => x !== o) : [...list, o])}
                                            className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${on
                                                ? 'border-[#2D8A8A] bg-[#2D8A8A] text-white'
                                                : 'border-gray-200 bg-white text-gray-600 hover:border-[#4ABDBD] hover:text-[#2D8A8A]'}`}
                                        >
                                            {o}
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {f.type === 'date' && (
                            <input
                                id={id}
                                data-testid={id}
                                type="date"
                                value={value ?? ''}
                                disabled={disabled}
                                aria-invalid={Boolean(err)}
                                aria-required={f.required === true}
                                onChange={(e) => onChange?.(f.key, e.target.value)}
                                className={`${inputBase} ${border}`}
                            />
                        )}

                        {f.type === 'yesno' && (
                            <div id={id} role="radiogroup" aria-label={f.label} data-testid={id} className="flex items-center gap-2">
                                {[true, false].map((choice) => (
                                    <button
                                        key={String(choice)}
                                        type="button"
                                        role="radio"
                                        aria-checked={value === choice}
                                        disabled={disabled}
                                        data-testid={`${id}-${choice ? 'yes' : 'no'}`}
                                        onClick={() => onChange?.(f.key, choice)}
                                        className={`min-w-[64px] rounded-xl border px-3 py-1.5 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${value === choice
                                            ? 'border-[#2D8A8A] bg-[#2D8A8A] text-white'
                                            : 'border-gray-200 bg-white text-gray-600 hover:border-[#4ABDBD] hover:text-[#2D8A8A]'}`}
                                    >
                                        {choice ? t('checkSets.yes') : t('checkSets.no')}
                                    </button>
                                ))}
                                {(value === true || value === false) && !disabled && (
                                    <button
                                        type="button"
                                        data-testid={`${id}-clear`}
                                        onClick={() => onChange?.(f.key, undefined)}
                                        className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-gray-500 hover:bg-gray-100"
                                    >
                                        <X className="h-3 w-3" aria-hidden="true" />
                                        {t('checkSets.clear')}
                                    </button>
                                )}
                            </div>
                        )}

                        {err && <p data-testid={`${id}-error`} className="text-xs text-red-600">{err}</p>}
                    </div>
                );
            })}
            {errors?._empty && (
                <p data-testid={`${idPrefix}-error-empty`} className="text-xs text-red-600 sm:col-span-2">{errorText('_empty')}</p>
            )}
        </div>
    );
}

export default ReadingForm;
